import { AuthProvider } from 'react-admin';
import { getSupabaseAuth } from '../lib/supabase';

// Supabase Auth 클라이언트 (Anon Key)
// 인증에는 Anon Key를 사용해야 합니다 (Service Role Key는 보안상 브라우저에서 사용 불가)
const supabase = getSupabaseAuth();

interface LoginParams {
  username: string;
  password: string;
}

export const supabaseAuthProvider: AuthProvider = {
  // 로그인
  login: async ({ username, password }: LoginParams) => {
    try {
      // 임시 하드코딩된 관리자 계정 (나중에 Supabase로 변경 필요)
      const hardcodedAdmins: Record<string, { password: string; id: string; name: string }> = {
        'test@test.com': {
          password: 'testtest1!',
          id: '71aa7ab5-4b3e-4cb0-b895-43a0913c0ec5',
          name: 'Admin User',
        },
        'dev@catchu.shop': {
          password: 'password123',
          id: '91b5193f-5a8a-468b-b4c2-63af21e3ee11',
          name: 'Dev Admin',
        },
      };

      const admin = hardcodedAdmins[username];
      if (admin && password === admin.password) {
        localStorage.setItem('adminUser', JSON.stringify({
          id: admin.id,
          email: username,
          role: 'admin',
          name: admin.name,
        }));
        localStorage.setItem('adminToken', 'temp-admin-token');
        return Promise.resolve();
      }

      // Supabase로 로그인 시도
      const { data, error } = await supabase.auth.signInWithPassword({
        email: username,
        password: password,
      });

      if (error) {
        throw new Error(error.message);
      }

      // 사용자 프로필 확인 (관리자인지)
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', data.user?.id)
        .single();

      if (profileError) {
        // 프로필이 없으면 기본 프로필로 진행
        const isAdmin = data.user?.email === 'admin@catchu.shop';
        if (!isAdmin) {
          await supabase.auth.signOut();
          throw new Error('관리자 권한이 없습니다.');
        }
      } else {
        // 관리자 권한 확인 (role이 admin인지)
        if (profile?.role !== 'admin' && profile?.role !== 'super_admin') {
          await supabase.auth.signOut();
          throw new Error('관리자 권한이 없습니다.');
        }
      }

      // 세션 정보 저장
      localStorage.setItem('adminUser', JSON.stringify({
        id: data.user?.id,
        email: data.user?.email,
        role: profile?.role || 'admin',
        name: profile?.name || data.user?.email?.split('@')[0],
      }));

      return Promise.resolve();
    } catch (error: any) {
      return Promise.reject(new Error(error.message || '로그인에 실패했습니다.'));
    }
  },

  // 로그아웃
  logout: async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminToken');
    return Promise.resolve();
  },

  // 인증 확인
  checkAuth: async () => {
    // 임시 토큰 확인
    const tempToken = localStorage.getItem('adminToken');
    if (tempToken === 'temp-admin-token') {
      return Promise.resolve();
    }

    // localStorage에 사용자 정보가 있는지 확인
    const adminUser = localStorage.getItem('adminUser');
    if (adminUser) {
      try {
        JSON.parse(adminUser);
        return Promise.resolve();
      } catch (error) {
        // 파싱 오류 시 로그아웃
        localStorage.removeItem('adminUser');
        localStorage.removeItem('adminToken');
        return Promise.reject(new Error('인증 정보가 손상되었습니다.'));
      }
    }

    // Supabase 세션 확인
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return Promise.reject(new Error('인증이 필요합니다.'));
    }
    
    return Promise.resolve();
  },

  // 에러 확인
  checkError: async (error: any) => {
    if (error?.status === 401 || error?.status === 403) {
      await supabase.auth.signOut();
      localStorage.removeItem('adminUser');
      return Promise.reject();
    }
    return Promise.resolve();
  },

  // 권한 가져오기
  getPermissions: async () => {
    const userStr = localStorage.getItem('adminUser');
    if (!userStr) {
      return Promise.reject();
    }

    try {
      const user = JSON.parse(userStr);

      // 역할별 권한 매핑
      const rolePermissions: Record<string, string[]> = {
        super_admin: ['*'], // 모든 권한
        admin: [
          'products.*',
          'categories.*',
          'orders.*',
          'users.*',
          'reviews.*',
        ],
      };

      const permissions = rolePermissions[user.role] || [];
      return Promise.resolve(permissions);
    } catch (error) {
      return Promise.reject();
    }
  },

  // 사용자 정보 가져오기
  getIdentity: async () => {
    // localStorage에서 관리자 정보 먼저 확인
    const userStr = localStorage.getItem('adminUser');
    if (userStr) {
      try {
        const adminUser = JSON.parse(userStr);
        return Promise.resolve({
          id: adminUser.id,
          fullName: adminUser.name || adminUser.email?.split('@')[0],
          avatar: undefined,
          email: adminUser.email,
          role: adminUser.role,
        });
      } catch (error) {
        // 파싱 오류 시 계속 진행
      }
    }

    // Supabase 사용자 정보 확인
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return Promise.reject();
    }

    const adminUser = userStr ? JSON.parse(userStr) : {};

    return Promise.resolve({
      id: user.id,
      fullName: adminUser.name || user.email?.split('@')[0],
      avatar: user.user_metadata?.avatar_url,
      email: user.email,
      role: adminUser.role,
    });
  },
};

export default supabaseAuthProvider;